import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Order, OrderItem } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, getOrderStatusTranslation } from '../utils/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);

  useEffect(() => {
    if (id) {
      fetchOrderDetail();
    }
  }, [id]);

  const fetchOrderDetail = async () => {
    try {
      const { data: orderData } = await supabase
        .from('habib_orders')
        .select('*, habib_users(username)')
        .eq('id', id)
        .single();

      const { data: itemsData } = await supabase
        .from('habib_order_items')
        .select(`
          *,
          product:habib_products(
            *,
            category:habib_categories(*)
          ),
          menu:habib_menu(
            *,
            menu_category:habib_menu_categories(*)
          )
        `)
        .eq('order_id', id);

      if (orderData) setOrder(orderData);
      if (itemsData) setOrderItems(itemsData);
    } catch (error) {
      console.error('Error fetching order detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (newStatus: string) => {
    // If marking as complete and order was previously pending, reduce stock
    if (newStatus === 'Pesanan Selesai' && order?.status === 'Permintaan Pesanan') {
      // Reduce stock for each PRODUCT item in the order (not menu items)
      for (const item of orderItems) {
        if (item.product_id) {
          // Get current stock from database
          const { data: product } = await supabase
            .from('habib_products')
            .select('stock')
            .eq('id', item.product_id)
            .single();
          
          if (product) {
            const currentStock = product.stock;
            const newStock = Math.max(0, currentStock - item.quantity);
            
            await supabase
              .from('habib_products')
              .update({ stock: newStock })
              .eq('id', item.product_id);
          }
        }
        // Note: Menu items don't have stock tracking, so we skip them
      }
    }
    
    await supabase
      .from('habib_orders')
      .update({ status: newStatus })
      .eq('id', id);
    
    setOrder(prev => prev ? { ...prev, status: newStatus as any } : null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{borderColor: '#05472A'}}></div>
          <p style={{color: '#05472A'}}>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
        <div className="text-center">
          <p style={{color: '#05472A'}}>Order not found</p>
          <button onClick={() => navigate('/admin')} className="mt-4 text-white px-6 py-3 rounded-xl" style={{backgroundColor: '#05472A'}}>
            {t('back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderColor: '#05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-0 sm:h-20 gap-4 sm:gap-0">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold" style={{color: '#05472A'}}>Order #{order.id}</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher />
              <button onClick={() => navigate('/admin')} className="bg-white hover:bg-green-50 border px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm flex items-center" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="hidden sm:inline">{t('back')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Order Header */}
          <div className="card-elevated overflow-hidden">
            <div className="p-4 sm:p-6 bg-gradient-to-r from-slate-50 to-slate-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-sm">#{order.id}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">{t('order')} #{order.id}</h2>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Customer: {(order as any).users?.username}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 2m8-2l2 2m-2-2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V9" />
                        </svg>
                        {new Date(order.created_at).toLocaleDateString()} {new Date(order.created_at).toLocaleTimeString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        {order.total_items} {t('items')}
                      </span>
                    </div>
                    {order.note && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-1">Order Note:</p>
                        <p className="text-sm text-blue-900">{order.note}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  <span className={`status-badge flex items-center gap-2 ${
                    order.status === 'Pesanan Selesai'
                      ? 'bg-success-100 text-success-700'
                      : 'bg-warning-100 text-warning-700'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={order.status === 'Pesanan Selesai' ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" : "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"} />
                    </svg>
                    {getOrderStatusTranslation(order.status, language)}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => updateOrderStatus('Permintaan Pesanan')}
                  disabled={order.status === 'Permintaan Pesanan'}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    order.status === 'Permintaan Pesanan'
                      ? 'bg-warning-500 text-white cursor-default'
                      : 'bg-white border border-warning-200 text-warning-700 hover:bg-warning-50 hover:border-warning-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('markAsPending')}
                </button>
                <button
                  onClick={() => updateOrderStatus('Pesanan Selesai')}
                  disabled={order.status === 'Pesanan Selesai'}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    order.status === 'Pesanan Selesai'
                      ? 'bg-success-500 text-white cursor-default'
                      : 'bg-white border border-success-200 text-success-700 hover:bg-success-50 hover:border-success-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('markAsCompleted')}
                </button>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="card-elevated">
            <div className="p-4 sm:p-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                {t('orderItems')} ({orderItems.length})
              </h3>
              <div className="grid gap-3 max-h-80 overflow-y-auto">
                {orderItems.map((item, index) => {
                  // Determine if this is a product or menu item
                  const isProduct = !!item.product_id;
                  const isMenu = !!item.menu_id;
                  
                  // Get the appropriate data
                  const photo = isProduct ? item.product?.photo : item.menu?.photo;
                  const name = isProduct ? item.product?.nama_produk : item.menu?.name;
                  const description = isProduct ? item.product?.deskripsi_produk : item.menu?.description;
                  
                  return (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl hover:from-slate-100 hover:to-slate-200 transition-all duration-300"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="relative">
                        <img 
                          src={photo || ''} 
                          alt={name || ''}
                          className="w-16 h-16 object-cover rounded-xl shadow-soft"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTI4IDI4TDM2IDM2IiBzdHJva2U9IiM5Q0EzQUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';
                          }}
                        />
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {item.quantity}
                        </div>
                        {/* Item type badge */}
                        <div className={`absolute -bottom-1 -left-1 px-2 py-0.5 rounded-md text-xs font-semibold ${
                          isMenu ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {isMenu ? '🍽️' : '📦'}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 mb-1 truncate">{name}</p>
                        <p className="text-sm text-slate-600 line-clamp-2">{description}</p>
                        {isMenu && item.menu?.menu_category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {item.menu.menu_category.name}
                          </span>
                        )}
                        {isProduct && item.product?.category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {item.product.category.name}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="bg-white px-3 py-2 rounded-lg shadow-soft">
                          <p className="text-xs text-slate-500 mb-1">{t('quantity')}</p>
                          <p className="font-bold text-slate-800 text-lg">{item.quantity}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderDetail;
